/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */

import { dateCache } from '../date-cache';
import { PLAN_CONSTANTS } from '../plan-constants';

/**
 * use active line and column width to guess x coordinates for as yet uncalculated columns
 *
 * Until we just fix this once and for all...
 * - if dayOffset is 0 that means you'll get the x for the finishDate
 * - if dayOffset is 1 that means you'll get the x for the startDate
 * - and because this is a number, a 2 means the value will shift one more
 * day to the left of the start date. And a -1 will shift one more day to the
 * right of the finish data (this has no practical purpose)
 */
export const guessXFromDate = (
	date: string,
	dayOffset: number = 0,
	activeLineDate: string,
	activeLineX: number,
	columnWidth: number = 145
): number => {
	const days = dateCache.diffDays(activeLineDate, date) + dayOffset;
	return activeLineX - columnWidth * days;
};

export const guessDateFromX = (
	x: number,
	activeLineX: number,
	activeLineXOffset = 0,
	activeLineDate: string = dateCache.todayString,
	columnWidth: number = 145
): string => {
	const activeLineXPoint = activeLineX + activeLineXOffset;
	const days = Math.floor(Math.round(Math.abs(activeLineXPoint - x)) / columnWidth);
	const d = dateCache.addDays(activeLineDate, -days);
	return d;
};

/**
 * Calculates the offset of the active line.
 *
 * This function determines the remainder when the absolute value of the
 * `activeLineX` coordinate is divided by the constant `ACTIVE_COLUMN_WIDTH`.
 * It is useful for determining where something has been dragged to within a
 * column.
 *
 * @param activeLineX - The X-coordinate of the active line.
 * @returns The offset of the active line.
 */
export const getActiveLineOffset = (activeLineX: number): number => {
	return Math.abs(activeLineX) % PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH;
};

// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7302
// This calculation will need to be updated once we are able to drag at a minute granularity
/**
 * Calculates a time offset in the format "hh:mm:ss" based on the provided `left` position
 * and the `activeLineX` position. If either `left` or `activeLineX` is undefined or null,
 * the function returns a default value of "00:00:00". Seconds are always set to "00".
 *
 * @param left - The left position (in pixels) relative to the active column. Can be undefined or null.
 * @param activeLineX - The X-coordinate of the active line (in pixels). Can be undefined or null.
 * @returns A string representing the calculated time offset in the format "hh:mm:ss".
 *
 * @remarks
 * - The function uses `PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH` to determine the width of the active column.
 * - The offset is calculated as a proportion of the active column width and converted into hours and minutes.
 * - Negative offsets are adjusted to ensure the returned time is valid.
 * - The function pads single-digit hours and minutes with leading zeros for proper formatting.
 */
export const guessTimeOffsetFromLeft = (
	left?: number | undefined,
	activeLineX?: number | undefined
): string => {
	if (left === undefined || left === null || activeLineX === undefined || activeLineX === null) {
		return '00:00:00';
	}

	const activeOffset = getActiveLineOffset(activeLineX);

	const offset = (left + activeOffset) % PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH;
	const offsetPortion = offset / PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH;
	const decimalHours = offsetPortion * 24;
	const hours = Math.floor(decimalHours);
	const minutes = Math.round((decimalHours % 1) * 60);
	const pad = (num: number): string => num.toString().padStart(2, '0');

	// Handle negative offsets by adjusting hours and minutes
	if (hours < 0 && minutes < 0) {
		return `${pad(hours + 24)}:${pad(minutes + 60)}:00`;
	}
	return `${pad(hours)}:${pad(minutes)}:00`;
};
// END PLACEHOLDER REPLACE AS PART OF TP-7302
